import { Moon } from "lucide-react";
import { formatTime, formatDuration, arrivesNextDay } from "@/components/trips/itinerary/itinerary-utils";
import { getAirportInfo } from "@/lib/airports";
import type { PlanFlight } from "@/lib/types";

/** Resolve human-readable label for an airport code: city > name > raw code. */
function airportTitle(code: string | null | undefined): string {
  if (!code) return "";
  const info = getAirportInfo(code);
  return info.city || info.name || code;
}

interface JourneyTimelineProps {
  flight: PlanFlight;
  /** Leg origin/destination IATA, used when per-segment data is absent. */
  originCode?: string | null;
  destinationCode?: string | null;
  /**
   * The longest durationMinutes across all flights in the current list.
   * When provided, the timeline bar scales proportionally so the longest
   * flight spans 100% and shorter flights are visually narrower (floor 45%).
   * Omit or pass null/0 to render at full width (single-flight or unknown context).
   */
  maxDurationMinutes?: number | null;
}

interface Stop {
  code: string | null;
  layover: string | null;
}

/** Intermediate stops with a layover label ONLY when segments supply it;
    otherwise N anonymous dots — never a fabricated airport or duration. */
function deriveStops(flight: PlanFlight): Stop[] {
  const segs = flight.segments?.length
    ? [...flight.segments].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    : null;
  if (segs && segs.length >= 2) {
    return segs.slice(0, -1).map((s) => ({
      code: (s.layoverAirport || s.destination || "").trim().toUpperCase() || null,
      layover: s.layoverMinutes != null ? formatDuration(s.layoverMinutes) : null,
    }));
  }
  return Array.from({ length: Math.max(0, flight.stops ?? 0) }, () => ({ code: null, layover: null }));
}

export default function JourneyTimeline({ flight, originCode, destinationCode, maxDurationMinutes }: JourneyTimelineProps) {
  const segs = flight.segments?.length
    ? [...flight.segments].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    : null;
  const depT = formatTime(flight.departLocal) || "—";
  const arrT = formatTime(flight.arriveLocal) || "—";
  const nextDay = arrivesNextDay(flight.departDate, flight.arriveDate);
  const total = formatDuration(flight.durationMinutes);
  const stops = deriveStops(flight);
  const stopsText = stops.length === 0 ? "Nonstop" : stops.length === 1 ? "1 stop" : stops.length + " stops";
  const showMoon = flight.redEye || flight.overnight;
  const origin = (segs ? segs[0].origin : originCode) || "";
  const dest = (segs ? segs[segs.length - 1].destination : destinationCode) || "";
  const knownLayovers = stops.filter((s) => s.code || s.layover);

  // Proportional width: longest flight in list = 100%; shorter flights scale down.
  // Floor of 45% keeps even very short flights readable.
  const pct =
    flight.durationMinutes && maxDurationMinutes && maxDurationMinutes > 0
      ? Math.max(45, Math.round((flight.durationMinutes / maxDurationMinutes) * 100))
      : 100;

  const dot = (cls: string, extra?: Record<string, string>) => (
    <span className={"h-2 w-2 shrink-0 rounded-full " + cls} aria-hidden="true" {...extra} />
  );

  return (
    <div data-journey-timeline className="flex items-center gap-2 text-xs" style={{ width: pct + "%" }}>
      <div className="shrink-0 text-right">
        <div className="font-mono tabular-nums font-medium text-foreground">{depT}</div>
        {origin && <div className="text-[11px] text-muted-foreground" title={airportTitle(origin) || undefined}>{origin}</div>}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center justify-center gap-1 text-[11px] tabular-nums text-muted-foreground">
          <span>{[total, stopsText].filter((x) => x && x !== "—").join(" · ") || stopsText}</span>
          {showMoon && (
            <Moon className="h-3 w-3 text-indigo-600" aria-label={flight.redEye ? "Red-eye flight" : "Arrives the next day"} />
          )}
        </div>
        <div className="flex items-center">
          {dot("bg-[hsl(var(--primary))]")}
          {stops.flatMap((stop, i) => [
            <span key={"l" + i} className="h-px flex-1 bg-[hsl(var(--primary))]/40" aria-hidden="true" />,
            <span
              key={"s" + i}
              className="h-2 w-2 shrink-0 rounded-full border-2 border-amber-500 bg-background"
              aria-hidden="true"
              data-stop=""
              title={stop.code ? airportTitle(stop.code) : "Connection · details pending"}
            />,
          ])}
          <span className="h-px flex-1 bg-[hsl(var(--primary))]/40" aria-hidden="true" />
          {dot("border-2 border-[hsl(var(--primary))] bg-background")}
        </div>
        {knownLayovers.length > 0 && (
          <div className="mt-0.5 text-center text-[10px] text-amber-700">
            {knownLayovers.map((s, i) => (
              <span key={i} data-layover className="mx-1 font-mono tabular-nums" title={s.code ? airportTitle(s.code) : undefined}>
                {[s.code, s.layover].filter(Boolean).join(" ")}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 text-left">
        <div className="font-mono tabular-nums font-medium text-foreground">
          {arrT}
          {arrT !== "—" && nextDay && <span className="ml-0.5 text-indigo-600">+1</span>}
        </div>
        {dest && <div className="text-[11px] text-muted-foreground" title={airportTitle(dest) || undefined}>{dest}</div>}
      </div>
    </div>
  );
}
